import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import { debounce } from 'lodash';
import { usePrevious } from 'hooks';
import { BaseModal } from 'components/UI';
import { truncateKeywordSpaces } from 'utils/utils';
import { Autocomplete } from 'components/FormFields';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';

const AsyncVehiclesGroupsSelect = ({
  id,
  type,
  label,
  margin,
  classes,
  variant,
  user_id,
  disabled,
  onChange,
  showModal,
  omitFields,
  asyncErrors,
  customParams,
  omitFieldKey,
  timezoneField,
  defaultVehicleGroup,
  field: { name, value },
  form: { errors, touched, setFieldValue },
  inputProps,
}) => {
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [defaultValue, setDefaultValue] = useState(value || '');
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...customParams,
  });

  const list = useSelector((state) => state.vehiclesGroups[type]);
  const loading = useSelector(
    (state) => state.vehiclesGroups[type === 'list' ? 'loadingList' : `${type}Loading`]
  );
  const prevvalue = usePrevious(value);
  const prevParams = usePrevious(customParams);

  const handleShowError = () => {
    if (asyncErrors) {
      return asyncErrors;
    } else if (errors && errors[name] && touched[name]) {
      return errors[name];
    }
  };

  const handleError = () => {
    if (asyncErrors) {
      return true;
    } else if (errors && errors[name] && touched && touched[name]) {
      return true;
    }
  };

  const fetchVehiclesGroups = useCallback(
    debounce((params) => {
      const newParams = { ...params, ...(user_id ? { user_id } : {}) };
      if (!newParams.keyword) delete newParams.keyword;

      setParams(newParams);
      Object.keys(newParams).forEach((key) => !newParams[key] && delete newParams[key]);
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      dispatch(VehiclesGroupsActions.fetchVehicleGroups(newParams, type));
    }, 1000)
  );

  useEffect(() => {
    if (user_id && !disabled) {
      const newParams = {
        ...params,
        ...customParams,
      };
      fetchVehiclesGroups(newParams);
    }
  }, [user_id, disabled]) // eslint-disable-line

  useEffect(() => {
    dispatch(VehiclesGroupsActions.resetVehicleGroups());
    if (defaultVehicleGroup)
      setDefaultValue(defaultVehicleGroup);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!modalOpen && prevvalue !== value) {
      let defaultVal = list.find((el) => el.id === value);

      if (defaultVehicleGroup) {
        setDefaultValue(defaultVehicleGroup);
      } else if (defaultVal) {
        setDefaultValue(defaultVal);
      } else if (value && !defaultValue?.id) {
        dispatch(VehiclesGroupsActions.fetchVehicleGroupById(value, null, 'list', type));
      } else {
        setDefaultValue(null);
      }
    }
  }, [value, list, modalOpen]) // eslint-disable-line

  useEffect(() => {
    if (prevParams && JSON.stringify(customParams) !== JSON.stringify(prevParams) && !disabled) {
      const newParams = {
        ...params,
        ...customParams,
      };
      setParams(newParams);
      fetchVehiclesGroups(newParams);
    }
  }, [customParams, disabled]) // eslint-disable-line

  useEffect(() => {
    if ((prevvalue === value || value) && list?.length > 0) {
      const defaultVal = list?.find((el) => el.id === parseInt(value));
      if (defaultVal) {
        setDefaultValue(defaultVal);
      }
    }
  }, [list]) // eslint-disable-line

  const handleShowOnSaveAndExitModal = () => {
    setFieldValue(name, defaultValue?.id || defaultValue || '');
    fetchVehiclesGroups({
      ...params,
      ...customParams,
      keyword: defaultValue?.id || defaultValue || '',
    });
    setModalOpen(false);
  };

  const handleFetchVehiclesGroup = () => {
    const newParams = {
      ...params,
      ...customParams,
    };
    if (list.length === 0 && !loading && !params?.keyword)
      dispatch(VehiclesGroupsActions.fetchVehicleGroups(newParams, type));
  };

  const getOptionLabel = (option) => {
    const groupTypeName =
      option?.group_type === 'OWNER'
        ? i18n.t('user_groups.owner_vehicle_group')
        : i18n.t('user_groups.vehicle_group');
    return groupTypeName + ' - ' + option?.name;
  };

  return (
    <>
      <Autocomplete
        id={id}
        name={name}
        label={label}
        margin={margin}
        fullwidth="true"
        variant={variant}
        classes={classes}
        disabled={disabled}
        onHighlightChange={handleFetchVehiclesGroup}
        clearOnBlur={false}
        value={defaultValue}
        inputProps={{...inputProps}}
        error={handleError()}
        helperText={handleShowError()}
        getOptionLabel={getOptionLabel}
        loading={loading}
        options={list?.filter((value) => omitFields.indexOf(value[omitFieldKey]) === -1)}
        onInputChange={(_, val) =>
          fetchVehiclesGroups({ ...params, ...customParams, keyword: val || '' })
        }
        onChange={(_, val) => {
          if (showModal && val) {
            setModalOpen(val);
          }
          if (!showModal) {
            setFieldValue(name, val?.id || '');
          }
          if (timezoneField) {
            setFieldValue(timezoneField, val?.time_zone || '');
          }
          if (onChange) {
            onChange(val);
          }
        }}
        getOptionSelected={(option, value) => option?.id === value?.id}
      />
      {modalOpen && (
        <BaseModal
          size="sm"
          open={modalOpen}
          hasEnterButton
          hasCancelButton
          enableCloseButton={false}
          title={i18n.t('common.are_you_sure')}
          enterButtonLabel={i18n.t('common.save')}
          children={i18n.t('vehicle_groups.change_service_area')}
          onEnter={() => {
            setFieldValue(name, modalOpen?.id || '');
            setModalOpen(false);
          }}
          onCancel={handleShowOnSaveAndExitModal}
          handleClick={handleShowOnSaveAndExitModal}
        />
      )}
    </>
  );
};

AsyncVehiclesGroupsSelect.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  label: PropTypes.string,
  margin: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  asyncErrors: PropTypes.string,
  customParams: PropTypes.object,
  defaultVehicleGroup: PropTypes.object,
  inputProps: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

AsyncVehiclesGroupsSelect.defaultProps = {
  type: 'list',
  required: true,
  disabled: false,
  margin: 'normal',
  form: {
    errors: {},
    touched: {},
  },
  field: {
    name: 'vehicleGroupsSelect',
  },
  inputProps: {},
  customParams: {},
  omitFields: [],
  omitFieldKey: '',
  label: 'Select...',
  variant: 'standard',
  defaultVehicleGroup: null,
  id: 'vehiclesGroupsSelect',
};

export default AsyncVehiclesGroupsSelect;
